import React, { useCallback, useEffect } from 'react';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import SelectableChip from 'retired/SelectableChip';
import {
  VolunteerRewardApprovalStatus,
  VolunteerRewardsQueryVariables,
  QueryMode,
  VolunteerRewardWhereInput,
  EligibilityFormStatus,
} from 'generated/graphql';

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 3;

export type FilterStatus = VolunteerRewardApprovalStatus | 'NON_DISBURSABLE';

const buildSearchQuery = (
  searchValue: string,
  filterStatus: FilterStatus
): { where: VolunteerRewardWhereInput } => {
  const baseQuery: VolunteerRewardWhereInput = {
    ...(filterStatus === 'NON_DISBURSABLE'
      ? {
          approvalStatus: { equals: VolunteerRewardApprovalStatus.Approved },
          OR: [{ donation: { equals: null } }, { donation: { isSet: false } }],
        }
      : {
          approvalStatus: filterStatus ? { equals: filterStatus } : undefined,
          ...(filterStatus === VolunteerRewardApprovalStatus.Approved
            ? {
                donation: {
                  isSet: true,
                  not: { equals: null },
                },
              }
            : {}),
          ...(filterStatus === VolunteerRewardApprovalStatus.Pending
            ? {
                formStatus: {
                  in: [EligibilityFormStatus.NotRequired, EligibilityFormStatus.Eligible],
                },
              }
            : {}),
        }),
  };

  if (searchValue.length < MIN_SEARCH_LENGTH) {
    return { where: baseQuery };
  }

  return {
    where: {
      ...baseQuery,
      User: {
        is: {
          OR: [
            { email: { contains: searchValue, mode: QueryMode.Insensitive } },
            { fullName: { is: { first: { contains: searchValue, mode: QueryMode.Insensitive } } } },
            { fullName: { is: { last: { contains: searchValue, mode: QueryMode.Insensitive } } } },
          ],
        },
      },
    },
  };
};

const options: { status: FilterStatus; label: string; icon?: React.ReactNode }[] = [
  { status: VolunteerRewardApprovalStatus.Pending, label: 'Pending' },
  { status: VolunteerRewardApprovalStatus.Approved, label: 'Approved' },
  { status: VolunteerRewardApprovalStatus.Rejected, label: 'Rejected' },
  {
    status: 'NON_DISBURSABLE',
    label: 'Non-disbursable',
    icon: <ErrorOutlineOutlinedIcon color="error" />,
  },
];

interface FiltersProps {
  setQueryVariables: (variables: VolunteerRewardsQueryVariables) => void;
  defaultQueryVariables: VolunteerRewardsQueryVariables;
  searchValue: string;
  selectedFilterStatus: FilterStatus;
  setSelectedFilterStatus: (status: FilterStatus) => void;
}

const Filters = ({
  setQueryVariables,
  defaultQueryVariables,
  searchValue,
  selectedFilterStatus,
  setSelectedFilterStatus,
}: FiltersProps): JSX.Element => {
  const updateQueryVariables = useCallback(
    (search: string, status: FilterStatus, resetPagination = false) => {
      setQueryVariables({
        ...defaultQueryVariables,
        ...buildSearchQuery(search, status),
        ...(resetPagination ? { skip: 0 } : {}),
      });
    },
    [defaultQueryVariables, setQueryVariables]
  );

  const debouncedSearch = useCallback(
    (value: string) => {
      updateQueryVariables(value, selectedFilterStatus);
    },
    [selectedFilterStatus, updateQueryVariables]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      debouncedSearch(searchValue);
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue, debouncedSearch]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ marginBottom: -8 }}>
          {options.map((option) => (
            <SelectableChip
              key={option.status || 'all'}
              label={option.label}
              icon={option.icon}
              onClick={(): void => {
                setSelectedFilterStatus(option.status);
                updateQueryVariables(searchValue, option.status, true);
              }}
              selected={selectedFilterStatus === option.status}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Filters;
